<script setup lang="ts">
import { AllComponents } from "@/builder/client/AllComponents";
import ButtonComponent from "@/builder/components/button/ButtonComponent.vue";
import ConfirmationRenderContainer from "@/builder/components/form/ConfirmationRenderContainer.vue";
import PromptRenderContainer from "@/builder/components/prompt/PromptRenderContainer.vue";
import { ComponentType, PartialDisplayType } from "@/builder/enums";
import { Gift, IConfirmationComponent, IFormComponent } from "@/builder/interfaces";
import { useRenderStore } from "@/builder/stores/renderStore";
import { IntegrationType } from "@/enums";
import { googleAnalyticsService } from "@/services/googleAnalyticsService";
import { publicApiService } from "@/services/publicApiService";
import { TrackingEventType, trackingPixelService } from "@/services/trackingPixelService";
import { PropType, computed, inject, onBeforeMount, onMounted, ref, watch } from "vue";
import FormModal from "../modal/FormModal.vue";
import TrustCenterContainer from "../trustCenter/TrustCenterContainer.vue";
import FormComponent from "./FormComponent.vue";

//props and services
const props = defineProps({
    component: {
        type: Object as PropType<IFormComponent>,
        required: true
    }
});

const renderStore = useRenderStore();
const trackingPixel = inject<trackingPixelService>("trackingPixelService") as trackingPixelService;
const googleAnalytics = inject<googleAnalyticsService>("googleAnalyticsService") as googleAnalyticsService;
const publicApi = inject<publicApiService>("publicApiService") as publicApiService;

// refs
const scrollTop = ref();
const trackingPixelUrl = renderStore.trackingPixelUrl;
const trackingPixelLoaded = ref(false);
const isMounted = ref(false);

// computed
const hasVirtuousIntegration = computed(() => renderStore.hasIntegration(IntegrationType.Virtuous));

const useTrackingPixel = computed(() => {
    return renderStore.page.useTrackingPixel && hasVirtuousIntegration.value && trackingPixelLoaded.value;
});

const hasGoogleAnalyticsIntegration = computed(() => renderStore.hasIntegration(IntegrationType.GoogleAnalytics));

const currentStepTitle = computed(() => {
    const currentStep = renderStore.getFormStepByIndex(renderStore.step);
    return currentStep?.customData.displayTitle ? currentStep.customData.title : null;
});

const confirmationComponent = computed(() => {
    return props.component.components.find(x => x.type === ComponentType.Confirmation) as IConfirmationComponent;
});

// methods
const initTrackingPixel = () => {
    if (!trackingPixelUrl) {
        console.error("Virtuous tracking pixel URL not found");
        return;
    }

    //if tracking pixel script already exists, do not inject again
    if (document.getElementById("virtuous-tracking-pixel")) {
        trackingPixelLoaded.value = true;
        return;
    }

    const virtuousIntegration = renderStore.getIntegration(IntegrationType.Virtuous);
    const config = { organizationId: virtuousIntegration?.publicKey };

    //inject tracking pixel script
    const script = document.createElement("script");
    script.async = true;
    script.id = "virtuous-tracking-pixel";
    script.type = "text/javascript";
    script.src = trackingPixelUrl;

    script.onload = () => {
        // @ts-ignore
        // eslint-disable-next-line
        virtuousTrackerShim.init(config)
    };

    document.head.appendChild(script);

    trackingPixelLoaded.value = true;
}

const initGoogleAnalytics = async () => {
    return new Promise<string | null>((resolve) => {
        const googleAnalyticsIntegration = renderStore.getIntegration(IntegrationType.GoogleAnalytics);

        if (!googleAnalyticsIntegration || !googleAnalytics) {
            resolve(null);
            return;
        }

        if (googleAnalyticsIntegration?.publicKey) {
            const script = document.createElement('script');
            script.async = true;
            script.setAttribute("src", "https://www.googletagmanager.com/gtag/js?id=" + googleAnalyticsIntegration.publicKey);
            document.head.appendChild(script);

            script.onload = async () => {
                const clientId = await googleAnalytics.init(googleAnalyticsIntegration.publicKey);
                resolve(clientId);
            }

            const dataLayerScript = document.createElement('script');
            dataLayerScript.appendChild(document.createTextNode(`
                window.dataLayer = window.dataLayer || [];
                function gtag(){dataLayer.push(arguments);}
                gtag('js', new Date());
                gtag('config', '${googleAnalyticsIntegration.publicKey}', { 'send_page_view': false });
            `));
            document.head.appendChild(dataLayerScript);
        } else {
            resolve(null);
        }
    });
};

const scrollToTopOfForm = () => {
    scrollTop.value.scrollIntoView({ block: "start", behavior: "smooth" });
};

//lifecycle hooks
onBeforeMount(() => {
    renderStore.setMaxSteps(props.component.components.filter(x => x.type === ComponentType.FormStep).length - 1);

    if (renderStore.page.useTrackingPixel && hasVirtuousIntegration.value) {
        initTrackingPixel();
    }
});

onMounted(async () => {
    if (renderStore.previewMode) return;

    if (useTrackingPixel.value && trackingPixel) {
        trackingPixel?.trackEvent(TrackingEventType.FormLoad, {
            formId: renderStore.page.id?.toString() ?? "",
            formName: renderStore.page.name,
        });
    }

    renderStore.clientId = await initGoogleAnalytics();

    if (hasGoogleAnalyticsIntegration.value && googleAnalytics) {
        renderStore.googleUtm = googleAnalytics.getUtmCodes();

        await googleAnalytics.trackPageView(
            renderStore.pageResponse.publicId ?? "",
            renderStore.organizationId,
            publicApi);
    }

    isMounted.value = true;
});

//watch renderstore donor for form start
watch(renderStore.donor, async (newDonor) => {
    if (newDonor && !renderStore.formStarted && isMounted.value) {
        renderStore.formStarted = true;

        if (useTrackingPixel.value && trackingPixel) {
            trackingPixel.trackEvent(TrackingEventType.FormStart, {
                formId: renderStore.pageResponse.publicId?.toString() ?? "",
                formName: renderStore.page.name,
            });
        }

        if (hasGoogleAnalyticsIntegration.value && googleAnalytics) {
            await googleAnalytics.trackFormStarted(
                renderStore.pageResponse.publicId ?? "",
                renderStore.organizationId,
                publicApi);
        }
    }
}, { deep: true });

//watch renderStore gift for form start
watch(renderStore.gift, async (newGift: Gift) => {
    if (newGift && newGift.amount && newGift.amount !== '0.00' && !renderStore.formStarted && isMounted.value) {
        renderStore.formStarted = true;

        if (useTrackingPixel.value && trackingPixel) {
            trackingPixel.trackEvent(TrackingEventType.FormStart, {
                formId: renderStore.pageResponse.publicId?.toString() ?? "",
                formName: renderStore.page.name,
            });
        }

        if (hasGoogleAnalyticsIntegration.value && googleAnalytics) {
            await googleAnalytics.trackFormStarted(
                renderStore.pageResponse.publicId ?? "",
                renderStore.organizationId,
                publicApi);
        }
    }
}, { deep: true });

watch(renderStore.errors, async (newErrors) => {
    if (newErrors.length === 0) return;
    if (renderStore.modelState && newErrors.length >= Object.keys(renderStore.modelState).length) {
        renderStore.moveToErrorStep();
    }
});
</script>

<template>
    <template v-if="renderStore.page.partialDisplayType === PartialDisplayType.Modal">
        <FormModal :component="props.component" :organization-id="renderStore.organizationId" />
    </template>

    <template v-else>
        <form name="rd-form" method="post" class="rd-form-wrapper form-wrapper" ref="scrollTop"
            :class="[component.styles?.boxShadow, component.styles?.cssClass]" :style="{
                backgroundColor: component.styles.backgroundColor || '',
                borderColor: component.styles.outlineColor || '',
                borderRadius: component.styles.borderRadius + 'px' || '',
                borderWidth: component.styles.outlineWeight + 'px' || '',
                color: component.styles.fontColor || '',
                padding: component.styles.padding + 'px' || ''
            }">
            <div
                :class="{ 'rd-items-center rd-flex rd-flex-row rd-w-full rd-mb-6': renderStore.step > 0 && !renderStore.success || currentStepTitle }">
                <!-- prev button -->
                <template
                    v-for="(item, index) in props.component.components.filter(x => x.type === ComponentType.FormStep)"
                    :key="index">
                    <template v-if="index === renderStore.step">
                        <ButtonComponent v-if="renderStore.step > 0 && !renderStore.success"
                            aria-label="Go to previous step" :component="item.customData.buttons.prev"
                            class="rd-btn-form" />
                    </template>
                </template>

                <template v-if="currentStepTitle && !renderStore.success">
                    <div class="form-step-title">
                        <span>{{ currentStepTitle }}</span>
                    </div>
                </template>
            </div>

            <template v-if="!renderStore.success">
                <section class="rd-form-container rd-w-full">
                    <FormComponent :component="props.component">
                        <template #children>
                            <template
                                v-for="(item, index) in props.component.components.filter(x => x.type === ComponentType.FormStep)"
                                :key="index">
                                <div :class="index === renderStore.step ? 'rd-block rd-w-full' : 'rd-hidden'">
                                    <component :is="AllComponents[item.type]" :component="item" />
                                </div>
                            </template>
                        </template>
                    </FormComponent>
                </section>

                <!-- next buttons -->
                <section v-if="renderStore.maxSteps > 0" class="rd-flex rd-justify-end rd-w-full"
                    :class="{ 'rd-mt-6': renderStore.step !== renderStore.maxSteps }">
                    <template
                        v-for="(item, index) in props.component.components.filter(x => x.type === ComponentType.FormStep)"
                        :key="index">
                        <template v-if="index === renderStore.step">
                            <div class="rd-flex rd-w-full"
                                :class="item.customData.buttons.next.customData.alignment && `rd-justify-${item.customData.buttons.next.customData.alignment}`">
                                <ButtonComponent v-if="renderStore.step !== renderStore.maxSteps"
                                    @click="scrollToTopOfForm" :component="item.customData.buttons.next"
                                    :disabled="renderStore.stepHasErrors(renderStore.step)" />
                            </div>
                        </template>
                    </template>
                </section>

                <!-- submit button -->
                <section v-if="renderStore.step === renderStore.maxSteps" class="rd-mt-10 rd-w-full submit-container">
                    <div class="rd-flex"
                        :class="`rd-justify-${component.customData.buttons.submit.customData.alignment}`">
                        <ButtonComponent :component="component.customData.buttons.submit"
                            :disabled="renderStore.canNotSubmit">
                            <template #buttonText>
                                {{ component.customData.buttons.submit.customData.text }} {{
                                    renderStore.submitButtonAmount }}
                            </template>
                        </ButtonComponent>
                    </div>
                </section>
            </template>

            <template v-else>
                <section class="rd-items-center rd-flex rd-flex-row rd-w-full">
                    <template
                        v-if="props.component.components.filter(x => x.type === ComponentType.Confirmation).length > 0">
                        <ConfirmationRenderContainer :component="confirmationComponent" />
                    </template>

                    <template v-else>
                        <div class="layout-row">
                            <div class="layout-col rd-w-full">
                                <h1>Thank you for your donation!</h1>
                            </div>
                        </div>
                    </template>
                </section>
            </template>

            <TrustCenterContainer :page="renderStore.page" />
        </form>
        <PromptRenderContainer />
    </template>
</template>
